import { create } from 'zustand'
import { ref as dbRef, onValue, set as dbSet, remove, update, get as dbGet, type Unsubscribe } from 'firebase/database'
import { collection, getDocs } from 'firebase/firestore'
import { rtdb, db } from '../firebase'

export interface PanicAlert {
  active: boolean
  triggeredAt: string
  location: { lat: number; lng: number } | null
  locationLabel: string | null
  locationAddress?: string | null
  inZoneName?: string | null
  profileId: string | null
  profileName: string | null
  clipInProgress?: boolean | null
}

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

async function fetchZoneName(uid: string, lat: number, lng: number): Promise<string | null> {
  try {
    const snap = await getDocs(collection(db, 'users', uid, 'safeZones'))
    for (const d of snap.docs) {
      const z = d.data() as { name: string; lat: number; lng: number; radius: number }
      if (haversineM(lat, lng, z.lat, z.lng) <= z.radius) return z.name
    }
  } catch (err: unknown) {
    console.warn('[panic.zoneNameFor] safezone lookup failed:', err)
  }
  return null
}

export type ResolveOutcome = 'safe' | 'false_alarm' | 'escalated' | 'referred'

export interface ResolvePanicDetails {
  outcome: ResolveOutcome
  note: string
  resolvedByUid: string
  resolvedByName: string
  resolvedByEmail?: string | null
  source?: 'command_center' | 'guardian' | 'org'
}

interface PanicState {
  isPanicActive: boolean
  isPending: boolean
  myPanicStatus: PanicAlert | null
  activePanicList: (PanicAlert & { uid: string })[]
  _uid: string | null
  _unsub: Unsubscribe | null
  _adminUnsub: Unsubscribe | null

  startListening: (uid: string) => void
  stopListening: () => void
  startAdminListening: () => void
  stopAdminListening: () => void
  triggerPanic: (profileId?: string, profileName?: string, getLocation?: () => Promise<{ lat: number; lng: number } | null>) => Promise<void>
  clearPanic: () => Promise<void>
  resolveAlert: (uid: string, details?: ResolvePanicDetails) => Promise<void>
}

export const usePanicStore = create<PanicState>()((set, get) => ({
  isPanicActive: false,
  isPending: false,
  myPanicStatus: null,
  activePanicList: [],
  _uid: null,
  _unsub: null,
  _adminUnsub: null,

  startListening(uid) {
    get()._unsub?.()
    set({ _uid: uid, isPending: true })

    const ref = dbRef(rtdb, `panic_status/${uid}`)
    const unsub = onValue(ref, (snap) => {
      const data = snap.exists() ? (snap.val() as PanicAlert) : null
      set({ myPanicStatus: data, isPanicActive: data?.active === true, isPending: false })
    }, () => {
      set({ myPanicStatus: null, isPanicActive: false, isPending: false })
    })

    set({ _unsub: unsub })
  },

  stopListening() {
    get()._unsub?.()
    set({ _uid: null, _unsub: null, myPanicStatus: null, isPanicActive: false, isPending: false })
  },

  startAdminListening() {
    get()._adminUnsub?.()
    const ref = dbRef(rtdb, 'panic_status')
    const unsub = onValue(ref, (snap) => {
      if (!snap.exists()) {
        set({ activePanicList: [] })
        return
      }
      const raw = snap.val() as Record<string, PanicAlert>
      const alerts = Object.entries(raw)
        .filter(([_, alert]) => alert.active)
        .map(([uid, alert]) => ({ ...alert, uid }))
      set({ activePanicList: alerts })
    })
    set({ _adminUnsub: unsub })
  },

  stopAdminListening() {
    get()._adminUnsub?.()
    set({ _adminUnsub: null, activePanicList: [] })
  },

  async resolveAlert(uid, details) {
    if (details) {
      const statusRef = dbRef(rtdb, `panic_status/${uid}`)
      const statusSnap = await dbGet(statusRef)
      const current = statusSnap.exists() ? (statusSnap.val() as PanicAlert) : null
      const resolvedAt = new Date().toISOString()
      const historyKey = Date.now().toString()

      await dbSet(dbRef(rtdb, `panic_history/${uid}/${historyKey}`), {
        type: 'resolved',
        resolvedAt,
        resolvedBy: {
          uid: details.resolvedByUid,
          name: details.resolvedByName,
          email: details.resolvedByEmail ?? null,
        },
        resolution: {
          outcome: details.outcome,
          note: details.note.trim(),
          source: details.source ?? 'command_center',
        },
        alert: current ? {
          triggeredAt: current.triggeredAt,
          profileId: current.profileId ?? null,
          profileName: current.profileName ?? null,
          location: current.location ?? null,
          locationLabel: current.locationLabel ?? null,
          locationAddress: current.locationAddress ?? null,
          inZoneName: current.inZoneName ?? null,
        } : null,
      })
    }

    await remove(dbRef(rtdb, `panic_status/${uid}`))
  },

  async triggerPanic(profileId, profileName, getLocation) {
    const uid = get()._uid
    if (!uid) throw new Error('Not authenticated')

    // Guard against repeat/looping calls for the same profile's already-active alert —
    // scoped to profileId (not just "any panic active") so a second dependent's panic
    // isn't blocked while a different one is still unresolved.
    const existing = get().myPanicStatus
    if (existing?.active && existing.profileId === (profileId ?? null)) return

    const triggeredAt = new Date().toISOString()
    set({
      isPanicActive: true,
      myPanicStatus: {
        active: true,
        triggeredAt,
        location: null,
        locationLabel: null,
        profileId: profileId ?? null,
        profileName: profileName ?? null,
      },
    })

    // Write immediately (no location yet) so recording can safely set clipInProgress after.
    await dbSet(dbRef(rtdb, `panic_status/${uid}`), {
      active: true,
      triggeredAt,
      location: null,
      locationLabel: null,
      profileId: profileId ?? null,
      profileName: profileName ?? null,
    } satisfies PanicAlert)

    // Get location in background and patch when ready.
    async function patchLocation(location: { lat: number; lng: number }) {
      update(dbRef(rtdb, `panic_status/${uid}`), { location }).catch((err) => console.warn('[panic] location update failed during active alert', err))
      try {
        const [data, inZoneName] = await Promise.all([
          fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.lat}&lon=${location.lng}`, {
            headers: { 'Accept-Language': 'en' },
          }).then(r => r.json()) as Promise<{ display_name?: string; address?: Record<string, string | undefined> }>,
          fetchZoneName(uid!, location.lat, location.lng),
        ])
        const a = data.address ?? {}
        const city = a.city || a.town || a.municipality || a.suburb || a.village || a.county || ''
        const province = a.province || a.state || a.region || ''
        const country = a.country || ''
        const label = [city, province, country].filter(Boolean).join(', ')
        const patch: Record<string, string | null> = {}
        if (label) patch.locationLabel = label
        if (data.display_name) patch.locationAddress = data.display_name
        if (inZoneName) patch.inZoneName = inZoneName
        if (Object.keys(patch).length) update(dbRef(rtdb, `panic_status/${uid}`), patch).catch((err) => console.warn('[panic] status patch failed during active alert', err))
      } catch (err: unknown) {
        console.warn('[panicStore] geocode fetch failed:', err)
      }
    }

    if (getLocation) {
      // Native: caller provides expo-location getter
      getLocation()
        .then(loc => { if (loc) patchLocation(loc) })
        .catch((err: unknown) => { console.warn('[panicStore] getLocation failed:', err) })
    } else if (typeof navigator !== 'undefined' && navigator.geolocation) {
      // Web: use browser geolocation API
      navigator.geolocation.getCurrentPosition(
        (pos) => patchLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err: GeolocationPositionError) => { console.warn('[panicStore] geolocation failed:', err) },
        { enableHighAccuracy: true, timeout: 5000 },
      )
    }
  },

  async clearPanic() {
    const uid = get()._uid
    if (!uid) throw new Error('Not authenticated')
    await remove(dbRef(rtdb, `panic_status/${uid}`))
  },
}))
